import { useFragment } from '@apollo/client';
import type { DeepPartial } from '@apollo/client/utilities';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Box,
  MenuItem,
  Stack,
  type SxProps,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useIsMobile } from '@side/pantry';
import { isSelectableTeamStatus } from '@/utils/team-status';
import {
  AccountSettingsTeamFieldsFragmentDoc,
  type GQLAccountSettingsTeamFieldsFragment,
} from '../AccountSettingsHeader/AccountSettingsHeader.generated';

type TeamSelectOptionProps = {
  teamId?: string;
  selected?: boolean;
};

const TeamSelectOption = ({ teamId, selected }: TeamSelectOptionProps) => {
  const { data: team } = useFragment<GQLAccountSettingsTeamFieldsFragment>({
    fragment: AccountSettingsTeamFieldsFragmentDoc,
    fragmentName: 'AccountSettingsTeamFields',
    from: {
      id: teamId,
      __typename: 'IdentityTeam',
    },
  });

  if (!team) return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        flex: 1,
        overflow: 'hidden',
      }}
    >
      {selected && (
        <VisibilityOutlinedIcon
          sx={{ width: 16, height: 16, color: 'action.active' }}
        />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant={selected ? 'body1' : 'body3'}
          noWrap
          sx={{
            color: 'text.primary',
            alignSelf: 'flex-start',
            maxWidth: '100%',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {team.alias ?? team.legalName}
        </Typography>
        {!selected && (
          <Typography variant="body3" sx={{ alignSelf: 'flex-start' }}>
            {team.supervisingBroker?.license?.state?.code}:{' '}
            {team.supervisingBroker?.license?.number}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

type ViewTeamSelectorProps = {
  teams: (
    | GQLAccountSettingsTeamFieldsFragment
    | DeepPartial<GQLAccountSettingsTeamFieldsFragment>
    | undefined
  )[];
  teamId?: string;
  handleTeamChange: (event: SelectChangeEvent) => void;
  sx?: SxProps<Theme>;
};
export const ViewTeamSelector = ({
  teams,
  teamId,
  handleTeamChange,
  sx,
}: ViewTeamSelectorProps) => {
  const isMobile = useIsMobile('md');
  const validTeams = teams.flatMap((t) =>
    t && isSelectableTeamStatus(t.status) ? t : [],
  );
  if (validTeams.length <= 1) {
    return null;
  }

  return (
    <TextField
      select
      fullWidth
      size={!isMobile ? 'small' : 'medium'}
      value={teamId || ''}
      sx={{
        ...(isMobile && {
          '& .MuiOutlinedInput-input': {
            paddingTop: '10.5px',
            paddingBottom: '10.5px',
          },
        }),
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          paddingRight: '32px !important',
        },
        ...sx,
      }}
      slotProps={{
        select: {
          'aria-label': 'Select Team',
          onChange: (event) => {
            handleTeamChange(event as SelectChangeEvent<string>);
          },
          renderValue: (selected) => (
            <TeamSelectOption teamId={selected as string} selected />
          ),
        },
      }}
    >
      {validTeams.map((team) => (
        <MenuItem key={team.id} value={team.id}>
          <TeamSelectOption teamId={team.id} />
        </MenuItem>
      ))}
    </TextField>
  );
};
